// src/constants/defaults.ts

import type { TextsConfig, CategoryDefinition, CookieConsentConfig, StyleConfig } from '../types'

export const DEFAULT_API_URL = 'https://cookies.meui.cz/api/config'

export const DEFAULT_TTL_DAYS = 365

export const DEFAULT_VERSION = '1.0.0'

// Style Presets
export const PRESETS: Record<string, { accentColor: string; style: Required<StyleConfig> }> = {
  // Classic clean design with icon
  default: {
    accentColor: '#3b82f6', // blue-500
    style: {
      position: 'bottom-left',
      maxWidth: 'md',
      spacing: 'normal',
      background: '#ffffff',
      borderRadius: 'rounded',
      shadow: 'xl',
      border: {
        width: '1',
        color: '#e5e7eb',
      },
      fontSize: {
        heading: 'lg',
        body: 'sm',
      },
      button: {
        shape: 'rounded',
        size: 'md',
      },
      modal: {
        shadow: 'xl',
      },
      animation: {
        direction: 'up',
        duration: 0.3,
        enabled: true,
      },
    },
  },

  // Original Meui design with inline settings
  meui: {
    accentColor: '#4A6953', // meui green
    style: {
      position: 'bottom-left',
      maxWidth: 'md',
      spacing: 'normal',
      background: '#ffffff',
      borderRadius: 'rounded',
      shadow: 'lg',
      border: {
        width: '1',
        color: '#e5e7eb',
      },
      fontSize: {
        heading: 'sm',
        body: 'xs',
      },
      button: {
        shape: 'pill',
        size: 'md',
      },
      modal: {
        shadow: '2xl',
      },
      animation: {
        direction: 'up',
        duration: 0.3,
        enabled: true,
      },
    },
  },

  // Bottom bar minimal design
  minimal: {
    accentColor: '#000000',
    style: {
      position: 'bottom-center',
      maxWidth: 'lg',
      spacing: 'compact',
      background: '#ffffff',
      borderRadius: 'sharp',
      shadow: 'none',
      border: {
        width: '1',
        color: '#000000',
      },
      fontSize: {
        heading: 'sm',
        body: 'xs',
      },
      button: {
        shape: 'square',
        size: 'sm',
      },
      modal: {
        shadow: 'lg',
      },
      animation: {
        direction: 'up',
        duration: 0.2,
        enabled: true,
      },
    },
  },

  // Card with header bar accent
  card: {
    accentColor: '#8b5cf6', // violet-500
    style: {
      position: 'bottom-right',
      maxWidth: 'sm',
      spacing: 'normal',
      background: '#ffffff',
      borderRadius: 'rounded',
      shadow: '2xl',
      border: {
        width: 'none',
        color: 'transparent',
      },
      fontSize: {
        heading: 'base',
        body: 'sm',
      },
      button: {
        shape: 'rounded',
        size: 'md',
      },
      modal: {
        shadow: '2xl',
      },
      animation: {
        direction: 'up',
        duration: 0.3,
        enabled: true,
      },
    },
  },

  // Full height sidebar
  sidebar: {
    accentColor: '#10b981', // emerald-500
    style: {
      position: 'bottom-right', // Note: sidebar overrides this internally
      maxWidth: 'sm',
      spacing: 'normal',
      background: '#ffffff',
      borderRadius: 'sharp',
      shadow: '2xl',
      border: {
        width: 'none',
        color: 'transparent',
      },
      fontSize: {
        heading: 'lg',
        body: 'sm',
      },
      button: {
        shape: 'rounded',
        size: 'md',
      },
      modal: {
        shadow: '2xl',
      },
      animation: {
        direction: 'right',
        duration: 0.3,
        enabled: true,
      },
    },
  },
}

// Default category definitions
export const DEFAULT_CATEGORIES: CategoryDefinition[] = [
  {
    id: 'strictly-necessary',
    label: 'Strictly Necessary',
    description: 'Essential cookies required for the website to function properly.',
    shortDescription: 'Essential for website functionality',
    longDescription:
      'These cookies are essential for the website to function properly. They enable basic features like page navigation and access to secure areas. The website cannot function properly without these cookies.',
    translations: {
      en: {
        label: 'Strictly Necessary',
        shortDescription: 'Essential for website functionality',
        longDescription:
          'These cookies are essential for the website to function properly. They enable basic features like page navigation and access to secure areas. The website cannot function properly without these cookies.',
      },
      cs: {
        label: 'Nezbytně nutné',
        shortDescription: 'Nezbytné pro fungování webu',
        longDescription:
          'Tyto cookies jsou nezbytné pro správné fungování webu. Umožňují základní funkce jako navigaci stránky a přístup k zabezpečeným oblastem. Web nemůže správně fungovat bez těchto cookies.',
      },
      de: {
        label: 'Unbedingt erforderlich',
        shortDescription: 'Für Website-Funktionalität erforderlich',
        longDescription:
          'Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich. Sie ermöglichen grundlegende Funktionen wie Seitennavigation und Zugriff auf sichere Bereiche. Die Website kann ohne diese Cookies nicht ordnungsgemäß funktionieren.',
      },
    },
    required: true,
    enabled: true,
  },
  {
    id: 'functional',
    label: 'Functional',
    description: 'Cookies that remember your preferences and choices.',
    shortDescription: 'Remember your preferences',
    longDescription:
      'These cookies enable enhanced functionality and personalization, such as remembering your preferences and choices. They may be set by us or by third-party providers.',
    translations: {
      en: {
        label: 'Functional',
        shortDescription: 'Remember your preferences',
        longDescription:
          'These cookies enable enhanced functionality and personalization, such as remembering your preferences and choices. They may be set by us or by third-party providers.',
      },
      cs: {
        label: 'Funkční',
        shortDescription: 'Pamatují si vaše předvolby',
        longDescription:
          'Tyto cookies umožňují rozšířenou funkcionalitu a personalizaci, jako je zapamatování vašich předvoleb a výběrů. Mohou být nastaveny námi nebo poskytovateli třetích stran.',
      },
      de: {
        label: 'Funktional',
        shortDescription: 'Speichern Ihre Einstellungen',
        longDescription:
          'Diese Cookies ermöglichen erweiterte Funktionalität und Personalisierung, wie das Speichern Ihrer Einstellungen und Auswahlen. Sie können von uns oder von Drittanbietern gesetzt werden.',
      },
    },
    required: false,
    enabled: false,
  },
  {
    id: 'analytics',
    label: 'Analytics',
    description: 'Cookies that help us understand how visitors interact with our website.',
    shortDescription: 'Help us improve',
    longDescription:
      'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website and provide better content.',
    translations: {
      en: {
        label: 'Analytics',
        shortDescription: 'Help us improve',
        longDescription:
          'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website and provide better content.',
      },
      cs: {
        label: 'Analytické',
        shortDescription: 'Pomáhají nám zlepšovat',
        longDescription:
          'Tyto cookies nám pomáhají pochopit, jak návštěvníci interagují s naším webem, tím že anonymně sbírají a hlásí informace. Pomáhá nám to vylepšovat náš web a poskytovat lepší obsah.',
      },
      de: {
        label: 'Analytisch',
        shortDescription: 'Helfen uns zu verbessern',
        longDescription:
          'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie Informationen anonym sammeln und melden. Dies hilft uns, unsere Website zu verbessern und bessere Inhalte bereitzustellen.',
      },
    },
    required: false,
    enabled: false,
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description: 'Cookies used for advertising and tracking across websites.',
    shortDescription: 'Personalized advertising',
    longDescription:
      'These cookies are used to track visitors across websites and display relevant advertisements. They may be used by advertising partners to build a profile of your interests.',
    translations: {
      en: {
        label: 'Marketing',
        shortDescription: 'Personalized advertising',
        longDescription:
          'These cookies are used to track visitors across websites and display relevant advertisements. They may be used by advertising partners to build a profile of your interests.',
      },
      cs: {
        label: 'Marketingové',
        shortDescription: 'Personalizovaná reklama',
        longDescription:
          'Tyto cookies slouží ke sledování návštěvníků napříč weby a zobrazování relevantních reklam. Mohou být použity reklamními partnery k vytvoření profilu vašich zájmů.',
      },
      de: {
        label: 'Marketing',
        shortDescription: 'Personalisierte Werbung',
        longDescription:
          'Diese Cookies werden verwendet, um Besucher über Websites hinweg zu verfolgen und relevante Werbung anzuzeigen. Sie können von Werbepartnern verwendet werden, um ein Profil Ihrer Interessen zu erstellen.',
      },
    },
    required: false,
    enabled: false,
  },
]

// Fallback translations
export const FALLBACK_TEXTS: Record<string, TextsConfig> = {
  en: {
    heading: 'We use cookies',
    description:
      'We use cookies to improve your experience on our website. By clicking "Accept", you consent to the use of cookies.',
    shortDescription: 'We use cookies to improve your experience.',
    longDescription:
      'We use cookies to improve your experience on our website. By clicking "Accept", you consent to the use of cookies. You can manage your preferences in the settings.',
    acceptButton: 'Accept all',
    declineButton: 'Decline',
    settingsButton: 'Settings',
    closeButton: 'Close',
    rejectLink: 'Reject all',
    settingsHeading: 'Cookie preferences',
    settingsDescription:
      'Choose which cookies you want to accept. You can change your preferences at any time.',
    saveButton: 'Save preferences',
    cancelButton: 'Cancel',
    categoryStrictlyNecessary: 'Strictly Necessary',
    categoryStrictlyNecessaryDesc:
      'Essential cookies required for the website to function properly.',
    categoryFunctional: 'Functional',
    categoryFunctionalDesc: 'Cookies that remember your preferences and choices.',
    categoryAnalytics: 'Analytics',
    categoryAnalyticsDesc:
      'Cookies that help us understand how visitors interact with our website.',
    categoryMarketing: 'Marketing',
    categoryMarketingDesc: 'Cookies used for advertising and tracking across websites.',
  },
  cs: {
    heading: 'Používáme cookies',
    description:
      'Používáme cookies ke zlepšení vašeho zážitku na našem webu. Kliknutím na "Přijmout vše" souhlasíte s používáním cookies.',
    shortDescription: 'Používáme cookies ke zlepšení vašeho zážitku.',
    longDescription:
      'Používáme cookies ke zlepšení vašeho zážitku na našem webu. Kliknutím na "Přijmout vše" souhlasíte s používáním cookies. Své preference můžete spravovat v nastavení.',
    acceptButton: 'Přijmout vše',
    declineButton: 'Odmítnout',
    settingsButton: 'Nastavení',
    closeButton: 'Zavřít',
    rejectLink: 'Odmítnout vše',
    settingsHeading: 'Nastavení cookies',
    settingsDescription:
      'Vyberte, které cookies chcete přijmout. Své preference můžete kdykoli změnit.',
    saveButton: 'Uložit předvolby',
    cancelButton: 'Zrušit',
    categoryStrictlyNecessary: 'Nezbytně nutné',
    categoryStrictlyNecessaryDesc: 'Základní cookies nezbytné pro správné fungování webu.',
    categoryFunctional: 'Funkční',
    categoryFunctionalDesc: 'Cookies, které si pamatují vaše předvolby a výběry.',
    categoryAnalytics: 'Analytické',
    categoryAnalyticsDesc: 'Cookies, které nám pomáhají pochopit, jak návštěvníci používají web.',
    categoryMarketing: 'Marketingové',
    categoryMarketingDesc: 'Cookies používané pro reklamu a sledování napříč weby.',
  },
  de: {
    heading: 'Wir verwenden Cookies',
    description:
      'Wir verwenden Cookies, um Ihre Erfahrung auf unserer Website zu verbessern. Durch Klicken auf "Alle akzeptieren" stimmen Sie der Verwendung von Cookies zu.',
    shortDescription: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern.',
    longDescription:
      'Wir verwenden Cookies, um Ihre Erfahrung auf unserer Website zu verbessern. Durch Klicken auf "Alle akzeptieren" stimmen Sie der Verwendung von Cookies zu. Sie können Ihre Einstellungen verwalten.',
    acceptButton: 'Alle akzeptieren',
    declineButton: 'Ablehnen',
    settingsButton: 'Einstellungen',
    closeButton: 'Schließen',
    rejectLink: 'Alle ablehnen',
    settingsHeading: 'Cookie-Einstellungen',
    settingsDescription:
      'Wählen Sie, welche Cookies Sie akzeptieren möchten. Sie können Ihre Präferenzen jederzeit ändern.',
    saveButton: 'Einstellungen speichern',
    cancelButton: 'Abbrechen',
    categoryStrictlyNecessary: 'Unbedingt erforderlich',
    categoryStrictlyNecessaryDesc:
      'Grundlegende Cookies, die für das ordnungsgemäße Funktionieren der Website erforderlich sind.',
    categoryFunctional: 'Funktional',
    categoryFunctionalDesc: 'Cookies, die sich Ihre Präferenzen und Auswahlen merken.',
    categoryAnalytics: 'Analytisch',
    categoryAnalyticsDesc:
      'Cookies, die uns helfen zu verstehen, wie Besucher mit der Website interagieren.',
    categoryMarketing: 'Marketing',
    categoryMarketingDesc:
      'Cookies, die für Werbung und Tracking über Websites hinweg verwendet werden.',
  },
}

// Default configuration (uses 'default' preset)
export const DEFAULT_CONFIG: Required<
  Omit<CookieConsentConfig, 'onAccept' | 'onDecline' | 'onChange' | 'onReady' | 'onBlock'>
> = {
  language: 'en',
  preset: 'default',
  accentColor: PRESETS.default.accentColor,
  borderRadius: PRESETS.default.style.borderRadius,
  style: {},
  showSettingsButton: true,
  closeButton: true,
  showRejectLink: true,
  mode: 'strict',
  domain: '',
  ttl: DEFAULT_TTL_DAYS,
  descriptionMode: 'short',
  links: {},
  customCategories: [],
  integrations: {},
  apiUrl: DEFAULT_API_URL,
  debug: false,
}

// Storage keys
export const STORAGE_KEYS = {
  CONSENT: 'meui_cookie_consent',
  CONFIG_CACHE: 'meui_cookie_config_cache',
  VERSION: 'meui_cookie_version',
} as const

// Event names
export const EVENTS = {
  CONSENT_GIVEN: 'meui:consent:given',
  CONSENT_REVOKED: 'meui:consent:revoked',
  CONSENT_CHANGED: 'meui:consent:changed',
  BANNER_SHOWN: 'meui:banner:shown',
  BANNER_HIDDEN: 'meui:banner:hidden',
  SETTINGS_OPENED: 'meui:settings:opened',
  SETTINGS_CLOSED: 'meui:settings:closed',
  SCRIPT_BLOCKED: 'meui:script:blocked',
  SCRIPT_UNBLOCKED: 'meui:script:unblocked',
} as const

// Cache TTL in milliseconds (24 hours)
export const CONFIG_CACHE_TTL = 24 * 60 * 60 * 1000
